import React, { useState, useCallback } from 'react'; // For React.ReactElement

import PropTypes from 'prop-types';
// import { useNavigate } from 'react-router-dom';

import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Text from '@zenbusiness/zui/Text';

import * as Styled from './UpsellCard.style';
import { CLICKSTREAM_COMMON_PROPS, PARTNER_LOGOS } from '../utils/constants';
import { highlightText } from '../utils/highlightText';

/**
 * A component for displaying upsell cards with consistent styling
 * @param {object} props - Component props
 * @param {string} [props.title] - The title of the upsell card
 * @param {string} [props.subtitle] - The subtitle text
 * @param {string} [props.description] - The description text
 * @param {string} [props.imageUrl] - URL of the image to display
 * @param {string} [props.badge] - Badge text (usually a number)
 * @param {string} [props.partner] - Partner name for styling (e.g., 'Avalara')
 * @param {string} [props.ctaText] - Text for the bottom button
 * @param {string} [props.ctaUrl] - URL to navigate to when button is clicked
 * @returns {React.ReactElement} The UpsellCard component
 */
function UpsellCard({
  title,
  subtitle,
  description,
  imageUrl,
  badge,
  partner,
  ctaText,
  ctaUrl
  // onClick
}) {
  const [imageLoaded, setImageLoaded] = useState(false);

  // console.log('[UpsellCard] ctaUrl:', onClick, ctaUrl);
  // const navigate = useNavigate();
  // const handleButtonClick = () => {
  //   if (onClick) {
  //     onClick();
  //   } else if (ctaUrl) {
  //     navigate(ctaUrl, {
  //       replace: true
  //     });
  //   }
  // };

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const handleImageError = useCallback(() => {
    setImageLoaded(true); // Still hide placeholder even on error
  }, []);

  const partnerLogo = PARTNER_LOGOS[partner];
  const hasContent = !!title || !!subtitle || !!description || !!badge || !!partner;
  return (
    <Styled.Root partner={partner}>
      <Styled.Content>
        <Box display="flex" flexDirection="column" gap={3}>
          {hasContent && (
            <Styled.TitleContainer>
              <Styled.SubtitleContainer>
                {title && (
                  <Styled.Title fontWeight="600" size="lg">
                    {title}
                  </Styled.Title>
                )}
                <Box>
                  {subtitle && <Styled.Subtitle size="sm">{subtitle}</Styled.Subtitle>}
                  {description && <Styled.Description size="sm">{description}</Styled.Description>}
                  {partner && partnerLogo && (
                    <Styled.PartnerContainer>
                      <Styled.PartnerLogo alt={partner} src={partnerLogo} />
                    </Styled.PartnerContainer>
                  )}
                </Box>
              </Styled.SubtitleContainer>
              {badge && (
                <Box>
                  <Styled.Badge partner={partner} size="sm">
                    <Text fontWeight="600" size="lg">
                      {badge}
                    </Text>
                    <Text size="xs">licenses</Text>
                  </Styled.Badge>
                </Box>
              )}
            </Styled.TitleContainer>
          )}
          {imageUrl && (
            <Styled.Media>
              {!imageLoaded && <Styled.ImagePlaceholder />}
              <Styled.Image
                alt={title || 'Upsell image'}
                src={imageUrl}
                style={{
                  opacity: imageLoaded ? 1 : 0,
                  transition: 'opacity 0.3s ease-in-out'
                }}
                onError={handleImageError}
                onLoad={handleImageLoad}
              />
            </Styled.Media>
          )}

          {ctaText && (
            <Styled.ButtonContainer>
              <Button
                clickstreamProps={{
                  ...CLICKSTREAM_COMMON_PROPS,
                  elementName: 'velo_upsell',
                  elementText: ctaText
                }}
                color="primary"
                fullWidth
                href={ctaUrl}
                size="medium"
                target="_blank"
                // onClick={handleButtonClick}
                variant="contained"
              >
                {highlightText(ctaText, Styled.TextHighlight)}
                <Styled.ArrowForwardIcon />
              </Button>
            </Styled.ButtonContainer>
          )}
        </Box>
      </Styled.Content>
    </Styled.Root>
  );
}

UpsellCard.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  description: PropTypes.string,
  imageUrl: PropTypes.string,
  badge: PropTypes.string,
  partner: PropTypes.string,
  ctaText: PropTypes.string,
  ctaUrl: PropTypes.string
  // onClick: PropTypes.func
};

export default UpsellCard;
